/**
 * Distancias a pie entre las estaciones y los puntos clave (oficina, almacén, torre, acceso
 * peatonal), para los recorridos de personas (paso 1.6, avance 2; doc. 04 §9; ADR 0021).
 *
 * Las personas caminan por la red de vías (también por las calzadas, como en el v6), con un tramo en
 * línea recta desde cada punto hasta la vía más cercana. Sin red o sin conexión entre dos puntos,
 * caminan en línea recta y la validación lo advierte.
 */

import type { Espacio, Punto } from '../dominio/espacio';
import { CODIGO_PUNTO, type PuntoDePersonas, type TipoEstacion } from '../dominio/proceso';
import {
  caminosMinimos,
  construirGrafo,
  puntoMasCercano,
  reconstruirCamino,
  tramosDe,
} from './circulacion';
import { dentroDelMarco } from './compilador';
import { distancia } from './geometria';

/** Elemento del catálogo de cada punto clave. */
export const CATALOGO_DE_PUNTO: Record<PuntoDePersonas, string> = {
  oficina: 'oficina-asesores',
  almacen: 'almacen-repuestos',
  torre: 'torre-control',
  'acceso-peatonal': 'acceso-peatonal',
};

export interface PuntoAPie {
  /** Código: el de la estación (B01, R01…) o el del punto clave (OF, ALM, TOR, PEA). */
  id: string;
  tipo: TipoEstacion | PuntoDePersonas;
  centro: Punto;
}

export interface DistanciasAPie {
  puntos: PuntoAPie[];
  /** Metros a pie entre puntos (índices de `puntos`). */
  distancias: number[][];
  /** Algún par se calculó en línea recta (sin red de vías o sin conexión). */
  enLineaRecta: boolean;
}

/** Puntos clave que hay en el espacio (el primero de cada tipo dentro del marco). */
export function puntosClave(espacio: Espacio): Map<PuntoDePersonas, Punto> {
  const r = new Map<PuntoDePersonas, Punto>();
  for (const [punto, catalogoId] of Object.entries(CATALOGO_DE_PUNTO) as [
    PuntoDePersonas,
    string,
  ][]) {
    const e = espacio.layout.elementos.find(
      (x) => x.catalogoId === catalogoId && dentroDelMarco(x, espacio.marco),
    );
    if (e) r.set(punto, e.posicion);
  }
  return r;
}

export function distanciasAPie(
  espacio: Espacio,
  estaciones: readonly { id: string; tipo: TipoEstacion; centro: Punto }[],
): DistanciasAPie {
  return redAPie(espacio, estaciones, false);
}

/**
 * Trazado de cada recorrido a pie entre dos puntos, por «desde>hacia» (B01>ALM…): el mismo camino que
 * mide `distanciasAPie`, para que la escena muestre a la persona caminando (paso 1.6, avance 2). Sin
 * conexión, en línea recta; de un punto a sí mismo, el punto.
 */
export function caminosAPie(
  espacio: Espacio,
  estaciones: readonly { id: string; tipo: TipoEstacion; centro: Punto }[],
): Record<string, Punto[]> {
  return redAPie(espacio, estaciones, true).caminos!;
}

function redAPie(
  espacio: Espacio,
  estaciones: readonly { id: string; tipo: TipoEstacion; centro: Punto }[],
  conCaminos: boolean,
): DistanciasAPie & { caminos?: Record<string, Punto[]> } {
  const puntos: PuntoAPie[] = estaciones.map((s) => ({ id: s.id, tipo: s.tipo, centro: s.centro }));
  for (const [punto, centro] of puntosClave(espacio))
    puntos.push({ id: CODIGO_PUNTO[punto], tipo: punto, centro });
  const n = puntos.length;
  const recta = (i: number, j: number) => distancia(puntos[i]!.centro, puntos[j]!.centro);
  const distancias = Array.from({ length: n }, (_, i) =>
    Array.from({ length: n }, (_, j) => recta(i, j)),
  );
  const caminos: Record<string, Punto[]> | undefined = conCaminos ? {} : undefined;
  const guardar = (i: number, j: number, medio: readonly Punto[]) => {
    if (!caminos) return;
    const trazo: Punto[] = [];
    for (const p of [puntos[i]!.centro, ...medio, puntos[j]!.centro]) {
      const u = trazo[trazo.length - 1];
      if (!u || u.x !== p.x || u.z !== p.z) trazo.push({ x: p.x, z: p.z });
    }
    caminos[`${puntos[i]!.id}>${puntos[j]!.id}`] = trazo;
  };
  const tramos = tramosDe(espacio.layout.vias, 'peatonal');
  if (tramos.length === 0) {
    for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) guardar(i, j, []);
    return { puntos, distancias, enLineaRecta: n > 1, ...(caminos ? { caminos } : {}) };
  }

  // Cada punto se une a la vía más cercana con un tramo en línea recta.
  const acercamientos = puntos.map((p) => puntoMasCercano(tramos, p.centro)!);
  const grafo = construirGrafo(
    espacio.layout.vias,
    acercamientos.map((a) => a.q),
    'peatonal',
  );
  let enLineaRecta = false;
  for (let i = 0; i < n; i++) {
    const origen = grafo.buscar(acercamientos[i]!.q);
    const red = origen === undefined ? null : caminosMinimos(grafo, origen);
    for (let j = 0; j < n; j++) {
      if (i === j) {
        distancias[i]![j] = 0;
        guardar(i, j, []);
        continue;
      }
      const destino = grafo.buscar(acercamientos[j]!.q);
      const enRed = red && destino !== undefined ? red.dist[destino]! : Infinity;
      if (Number.isFinite(enRed)) {
        distancias[i]![j] = acercamientos[i]!.d + enRed + acercamientos[j]!.d;
        if (caminos)
          guardar(
            i,
            j,
            reconstruirCamino(red!.previo, destino!).map((k) => grafo.nodos[k]!),
          );
      } else {
        enLineaRecta = true;
        guardar(i, j, []);
      }
    }
  }
  return { puntos, distancias, enLineaRecta, ...(caminos ? { caminos } : {}) };
}
